<template>
  <div class="w-full profile-card-container">
    <div class="profile-card">
      <div class="top-gradient"></div>
      <div class="stars-container">
        <div class="star" v-for="i in 15" :key="i"></div>
      </div>
      <div class="profile-content">
        <div class="avatar-section">
          <div class="orbit orbit-1"></div>
          <div class="orbit orbit-2"></div>
          <div class="mini-planet planet-1">🌟</div>
          <div class="mini-planet planet-2">✨</div>
          <div class="mini-planet planet-3">💫</div>
          <div class="avatar-wrapper">
            <div class="avatar-glow"></div>
            <img :src="profile.avatar" :alt="profile.name" class="avatar" />
          </div>
        </div>
        <div class="info-section">
          <h3 class="profile-name">{{ profile.name }}</h3>
          <p class="profile-tagline">
            🪐 too young too simple, sometimes naive
          </p>
        </div>
        <div class="divider"></div>
        <nav class="nav-section">
          <a
            v-for="link in profile.links"
            :key="link.label"
            :href="link.href"
            :target="link.external ? '_blank' : undefined"
            class="nav-item"
          >
            <div class="nav-icon-wrapper">
              <component :is="getIcon(link.icon)" class="nav-icon" />
            </div>
            <span class="nav-label">{{ link.label }}</span>
            <svg
              class="nav-arrow"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </a>
        </nav>
        <div class="footer-section">
          <div class="footer-decoration">
            <span class="decoration-dot"></span>
            <span class="decoration-line"></span>
            <span class="decoration-dot"></span>
          </div>
          <div class="social-links">
            <a
              v-for="social in socialLinks.filter(
                (s) => s.icon === 'github' || s.icon === 'email'
              )"
              :key="social.name"
              :href="social.url"
              :aria-label="social.ariaLabel"
              class="social-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg
                class="social-icon"
                :fill="social.icon === 'email' ? 'none' : 'currentColor'"
                :stroke="social.icon === 'email' ? 'currentColor' : 'none'"
                viewBox="0 0 24 24"
                v-html="getSocialIcon(social.icon)"
              ></svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, h } from "vue";
import { HomeOutline, PlanetOutline, MailOutline } from "@vicons/ionicons5";
import { useProfile } from "@/composables/useProfile";
import { socialLinks, getSocialIcon } from "@/config/siteConfig";

export default defineComponent({
  name: "ProfileCard",
  setup() {
    const { profile } = useProfile();
    const iconMap = {
      home: defineComponent({ render: () => h(HomeOutline) }),
      planet: defineComponent({ render: () => h(PlanetOutline) }),
      contact: defineComponent({ render: () => h(MailOutline) }),
    };
    const getIcon = (iconName) => iconMap[iconName];
    return {
      profile,
      getIcon,
      socialLinks,
      getSocialIcon,
    };
  },
});
</script>

<style scoped>
.profile-card-container {
  display: block;
  width: 100%;
}
.profile-card {
  position: relative;
  background: white;
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  width: 100%;
}
.profile-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}
.top-gradient {
  height: 4px;
  background: linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899);
  position: relative;
  overflow: hidden;
}
.top-gradient::after {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.8),
    transparent
  );
  animation: shimmer 3s infinite;
}
@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 200%;
  }
}
.stars-container {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}
.star {
  position: absolute;
  width: 2px;
  height: 2px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  border-radius: 50%;
  opacity: 0.3;
  animation: twinkle 3s infinite;
}
.star:nth-child(1) {
  top: 15%;
  left: 10%;
  animation-delay: 0s;
}
.star:nth-child(2) {
  top: 25%;
  left: 85%;
  animation-delay: 0.5s;
}
.star:nth-child(3) {
  top: 35%;
  left: 30%;
  animation-delay: 1s;
}
.star:nth-child(4) {
  top: 45%;
  left: 70%;
  animation-delay: 1.5s;
}
.star:nth-child(5) {
  top: 55%;
  left: 15%;
  animation-delay: 2s;
}
.star:nth-child(6) {
  top: 65%;
  left: 80%;
  animation-delay: 2.5s;
}
.star:nth-child(7) {
  top: 75%;
  left: 45%;
  animation-delay: 0.3s;
}
.star:nth-child(8) {
  top: 20%;
  left: 60%;
  animation-delay: 0.8s;
}
.star:nth-child(9) {
  top: 40%;
  left: 25%;
  animation-delay: 1.3s;
}
.star:nth-child(10) {
  top: 60%;
  left: 65%;
  animation-delay: 1.8s;
}
.star:nth-child(11) {
  top: 30%;
  left: 90%;
  animation-delay: 0.2s;
}
.star:nth-child(12) {
  top: 50%;
  left: 5%;
  animation-delay: 0.7s;
}
.star:nth-child(13) {
  top: 70%;
  left: 55%;
  animation-delay: 1.2s;
}
.star:nth-child(14) {
  top: 10%;
  left: 40%;
  animation-delay: 1.7s;
}
.star:nth-child(15) {
  top: 80%;
  left: 20%;
  animation-delay: 2.2s;
}
@keyframes twinkle {
  0%,
  100% {
    opacity: 0.2;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.5);
  }
}
.profile-content {
  position: relative;
  z-index: 1;
  padding: 2rem 1.75rem;
}
.avatar-section {
  position: relative;
  width: 140px;
  height: 140px;
  margin: 0 auto 1.5rem;
}
.orbit {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: 50%;
  animation: rotate-orbit 25s linear infinite;
}
.orbit-1 {
  width: 130px;
  height: 130px;
}
.orbit-2 {
  width: 110px;
  height: 110px;
  animation-duration: 20s;
  animation-direction: reverse;
}
@keyframes rotate-orbit {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}
.mini-planet {
  position: absolute;
  font-size: 0.875rem;
  animation: float-planet 4s ease-in-out infinite;
}
.planet-1 {
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  animation-delay: 0s;
}
.planet-2 {
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  animation-delay: 1.3s;
}
.planet-3 {
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  animation-delay: 2.6s;
}
@keyframes float-planet {
  0%,
  100% {
    transform: translate(-50%, 0) scale(1);
    opacity: 0.6;
  }
  50% {
    transform: translate(-50%, -8px) scale(1.2);
    opacity: 1;
  }
}
.avatar-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90px;
  height: 90px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid white;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.2),
    0 0 0 1px rgba(59, 130, 246, 0.1);
  transition: all 0.3s ease;
}
.profile-card:hover .avatar-wrapper {
  transform: translate(-50%, -50%) scale(1.08);
  box-shadow: 0 6px 30px rgba(59, 130, 246, 0.3),
    0 0 0 1px rgba(59, 130, 246, 0.2);
}
.avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.avatar-glow {
  position: absolute;
  inset: -15px;
  background: radial-gradient(
    circle,
    rgba(59, 130, 246, 0.15) 0%,
    transparent 70%
  );
  animation: pulse-glow 3s ease-in-out infinite;
  pointer-events: none;
}
@keyframes pulse-glow {
  0%,
  100% {
    opacity: 0.5;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
}
.info-section {
  text-align: center;
  margin-bottom: 1.5rem;
}
.profile-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
}
.profile-tagline {
  font-size: 0.875rem;
  color: #6b7280;
  font-style: italic;
}
.divider {
  width: 64px;
  height: 3px;
  background: linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899);
  border-radius: 2px;
  margin: 0 auto 1.5rem;
}
.nav-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}
.nav-item {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 12px;
  text-decoration: none;
  color: #374151;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}
.nav-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(to bottom, #3b82f6, #8b5cf6);
  transform: scaleY(0);
  transition: transform 0.3s ease;
}
.nav-item:hover {
  background: white;
  border-color: rgba(59, 130, 246, 0.2);
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
}
.nav-item:hover::before {
  transform: scaleY(1);
}
.nav-icon-wrapper {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  border-radius: 8px;
  flex-shrink: 0;
  transition: all 0.3s ease;
}
.nav-item:hover .nav-icon-wrapper {
  transform: scale(1.1) rotate(-5deg);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}
.nav-icon {
  width: 1.125rem;
  height: 1.125rem;
  color: white;
}
.nav-label {
  flex: 1;
  font-size: 0.9375rem;
  font-weight: 500;
}
.nav-arrow {
  width: 1rem;
  height: 1rem;
  color: #9ca3af;
  transition: all 0.3s ease;
  flex-shrink: 0;
}
.nav-item:hover .nav-arrow {
  color: #3b82f6;
  transform: translateX(4px);
}
.footer-section {
  text-align: center;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
}
.footer-decoration {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.decoration-dot {
  width: 4px;
  height: 4px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  border-radius: 50%;
  animation: pulse-dot 2s ease-in-out infinite;
}
.decoration-dot:nth-child(1) {
  animation-delay: 0s;
}
.decoration-dot:nth-child(3) {
  animation-delay: 1s;
}
@keyframes pulse-dot {
  0%,
  100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.5);
  }
}
.decoration-line {
  width: 40px;
  height: 1px;
  background: linear-gradient(to right, #3b82f6, #8b5cf6);
}
.social-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}
.social-link {
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f9fafb, #ffffff);
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  color: #6b7280;
  transition: all 0.3s ease;
  text-decoration: none;
  position: relative;
  overflow: hidden;
}
.social-link::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  opacity: 0;
  transition: opacity 0.3s ease;
}
.social-link:hover {
  transform: translateY(-3px) scale(1.05);
  border-color: rgba(59, 130, 246, 0.3);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}
.social-link:hover::before {
  opacity: 1;
}
.social-icon {
  width: 1.125rem;
  height: 1.125rem;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
}
.social-link:hover .social-icon {
  color: white;
  transform: scale(1.1);
}
.footer-text {
  font-size: 0.75rem;
  color: #9ca3af;
  font-style: italic;
}
@media (max-width: 1024px) {
  .profile-content {
    padding: 1.75rem 1.5rem;
  }
  .avatar-section {
    width: 120px;
    height: 120px;
  }
  .avatar-wrapper {
    width: 80px;
    height: 80px;
  }
  .orbit-1 {
    width: 110px;
    height: 110px;
  }
  .orbit-2 {
    width: 95px;
    height: 95px;
  }
}
</style>
